/**
 * The "pinned moment" contract shared by every trace surface — the Dashboard's
 * Cognitive trace (participant + tenant admin) and the Analysis overlay's
 * focus / hand-motion charts.
 *
 * One moment is pinned at a time: clicking a chart or picking a transcript
 * line marks it and plays the media there, and clicking the moment you are
 * already on toggles that playback off (and on again). Keeping the tolerance
 * and the stamp format here means a surface can't quietly disagree with the
 * others about what counts as "the same moment".
 */

/**
 * Two picks are the same moment within this many seconds. Chart clicks snap to
 * a data point and caption picks carry an exact timestamp, so this only
 * absorbs float noise from trace re-binning — it is not a hit radius, and it
 * is deliberately far below the ~1 s spacing of trace points so two genuinely
 * different instants can never collapse into one.
 */
export const SAME_MOMENT_EPS_S = 0.25;

export function isSameMoment(a: number | null, b: number | null): boolean {
  if (a === null || b === null) return false;
  return Math.abs(a - b) <= SAME_MOMENT_EPS_S;
}

/** m:ss for a session-elapsed second — the Dashboard trace's axis units. */
export function formatElapsedStamp(t: number): string {
  const s = Math.max(0, Math.round(t));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
